import UploadDocumentation from '../components/fields/UploadDocumentation';
import { getValueSubmit } from '../components/values/Get';
import { isElementVisible } from '../conditional-fields/evaluate-field-condition';
import { conf } from '../config/config';
import { TopConfig } from '../context/ConfigContext';
import { getArrayOfPagesShown } from '../helpers/getPageInfo';
import { submitForm } from '../services/submit';
import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { StagedFile } from '../types/services';
import { saveFormDataDelete } from '../services/saveFormData';
import ContactEmail from '../components/custom/ContactEmail';
import ContactPhone from '../components/custom/ContactPhone';
import ContactUpdateProfile from '../components/custom/ContactUpdateProfile';
import { logError } from '../utils/logger';
import {
    trackNavigationToReceiptPage,
    trackSubmissionFail,
    trackSubmissionSuccess,
    trackTimeSpentOnFillingTheForm
} from '../utils/tracking';
import { ClaimFormPayload } from '../types/claim-form-payload';
import { ClaimFormField } from '../types/claim-form-field';
import { getTrackBySubmitFunction, hasTrackingBySubmitFunction } from './getTrackBySubmitFunction';
import { pingSession } from '../services/session';
import { trackInfoEventValue } from '../utils/trackingWrapper';

// Gather all form values and convert
// them to the existing claim-form format
// to be able to submit without changing the backend
export const getSubmitPayload = async (
    formConfig: FormConfig,
    formValues: FormValues,
    stagedFiles: StagedFile[],
    cmsResponseTime: string,
    claimNumber: string | ''
) => {
    try {
        const { Stakeholder } = formConfig;
        const actions = getActions(formConfig, formValues);

        let assignmentGroup = formConfig.automation.assignmentGroup;
        const claimCategory = formConfig.automation.claimCategory;
        const segment = formConfig.automation.segment;
        const highwayPath = formConfig.automation.highwayPath;
        const lossType = formConfig.automation.lossType;
        const properties = formConfig.properties;

        const { emailHeader, emailBody, emailSender, emailSubject } = getEmailFields(formConfig, cmsResponseTime);

        // If the form is the 'andet-privat' form, we need to override the default assignmentGroup specified in the form config,
        // with the one specified by the customer when filling out the form.
        if (formConfig.formId === 'andet-privat') {
            assignmentGroup = formValues['customAssignmentGroup'].value;
        }

        const payload: ClaimFormPayload = {
            ClaimNumber: claimNumber,
            Properties: { properties },
            HighwayPath: highwayPath,

            Actions: actions,
            Stakeholder: getStakeholderFields(Stakeholder, formValues),
            Brand: 'td',
            ClaimFields: [],
            AssignmentGroup: assignmentGroup,
            ClaimCategory: claimCategory,
            Segment: segment,
            LossType: lossType,

            Email: { uid: '', header: emailHeader, intro: emailBody, title: emailSubject, sender: emailSender },
            Channel: 'web'
        };

        payload.ClaimFields = await getClaimFields(formConfig, formValues, stagedFiles, Stakeholder);

        return payload;
    } catch (error) {
        throw new Error('Error getting the submit payload: ' + JSON.stringify(error, null, 4));
    }
};

const getStakeholderFields = (stakeholderIDs: string[], formValues: FormValues) => {
    const stakeholders = stakeholderIDs.reduce((stakeholder, id) => {
        let key = id;
        if (id.includes('email')) {
            key = 'Email';
        } else if (id.includes('phone')) {
            key = 'Phone';
        } else if (id.includes('name')) {
            key = 'Name';
        } else if (id.includes('cprCvr')) {
            key = 'CprCvr';
        }
        return { ...stakeholder, [key]: formValues[id] };
    }, {});

    return stakeholders;
};

// Iterate over all pages in the order they were shown
// Determine summaryGroupId they belong to
// Add the fields to that group
const getClaimFields = async (
    formConfig: FormConfig,
    formValues: FormValues,
    stagedFiles: StagedFile[],
    stakeholders: string[]
): Promise<ClaimFormField[]> => {
    const claimFields: ClaimFormField[] = [];

    const pagesShown = await getArrayOfPagesShown(formConfig, formValues);

    // summaryGroups: [
    //     {
    //         summaryGroupId: 'aboutTheDamage',
    //         caption: 'Om skaden'
    //     },
    //     {
    //         summaryGroupId: 'documentation',
    //         caption: 'Dokumentation'
    //     },
    //     {
    //         summaryGroupId: 'aboutYou',
    //         caption: 'Om dig'
    //     }
    // ],
    const { summaryGroups } = formConfig;

    summaryGroups.forEach((summaryGroup) => {
        const { summaryGroupId, caption: summaryGroupCaption } = summaryGroup;

        const claimField: ClaimFormField = {
            section: summaryGroupCaption,
            fields: []
        };

        formConfig.pages.forEach((page) => {
            const { elements } = page;

            if (!pagesShown.includes(page.pageId)) {
                return;
            }

            elements.forEach((element) => {
                const { summaryCaption, elementId } = element;

                // If there is no summaryGroup, there is no value
                // Eg. LandingPage, ContentBlock etc
                if (element.summaryGroupId === undefined || summaryCaption === undefined) {
                    return;
                }

                // Skip the elements that do not belong to
                // the summaryGroupId we are looking for
                if (element.summaryGroupId !== summaryGroupId) {
                    return;
                }

                // Filter out the conditionally invisible fields
                if (!isElementVisible(formConfig, formValues, elementId)) {
                    return;
                }

                //Filter out StakeholderFields as those fiels will be submitted in the Stakeholder{} payload
                if (stakeholders.some((id) => id === elementId)) {
                    return;
                }

                const claimFieldValue = getValueSubmit(formConfig, formValues, elementId, summaryCaption, stagedFiles);

                if (claimFieldValue !== undefined) {
                    if (Array.isArray(claimFieldValue)) {
                        claimField.fields = [...claimField.fields, ...claimFieldValue];
                    } else {
                        claimField.fields.push(claimFieldValue);
                    }
                }
            });
        });
        // Add fields for the summary group to claimFields array
        // if the summary group has >0 fields
        if (claimField.fields.length) {
            claimFields.push(claimField);
        }
    });
    return claimFields;
};

const getActions = (formConfig: FormConfig, formValues: FormValues) => {
    const actions: ClaimFormPayload['Actions'] = [];

    const { elementIdEmail, elementIdPhone, elementIdContactUpdateProfile } = formConfig;

    const phone = ContactEmail.getValue(formValues, elementIdPhone);
    const email = ContactPhone.getValue(formValues, elementIdEmail);

    const updateContact = ContactUpdateProfile.getValue(formValues, elementIdContactUpdateProfile);

    if (updateContact === true) {
        actions.push({ type: 'updateProfileEmail', payload: email });
        actions.push({ type: 'updateProfilePhone', payload: phone });
    }

    return actions;
};

const getEmailFields = (formConfig: FormConfig, cmsResponseTime: string) => {
    const receiptEmailKey = formConfig.automation.receiptEmail;

    const emailData = conf.receiptEmails.find((r) => r.id === receiptEmailKey);

    // The error is not shown in the console...
    if (!emailData) {
        console.error('Invalid email receipt key: ' + receiptEmailKey);
        throw new Error('Invalid email receipt key: ' + receiptEmailKey);
    }

    const emailHeader = emailData.email.header;
    const emailSender = emailData.email.from;
    const emailSubject = emailData.email.subject;

    // the expected response time can be edited and fetched from the CMS at:
    // dk-sites->administration->labels->Hybrid APPs->Skade->Skade anmeldelse->Claim response time
    const emailBody = emailData.email.body.replace('{response.time}', cmsResponseTime);

    return { emailHeader, emailBody, emailSender, emailSubject };
};

export const onSubmit = async (
    formConfig: FormConfig,
    formValues: FormValues,
    topConfig: TopConfig,
    formStartTime: number | undefined,
    claimNumber: string,
    cmsResponseTime: string,
    resetError: () => void,
    setError: (error: any) => void,
    resetForm: () => void,
    setIsSubmitting: (submitting: boolean) => void,
    setIsRedirecting: (isRedirecting: boolean) => void,
    setClaimFormPayload: (claimFormPayload: ClaimFormPayload) => void
) => {
    setIsSubmitting(true);
    resetError();

    try {
        const isMitPage = topConfig.siteConfig?.siteType === 'mit';
        if (isMitPage) {
            const isLoggedIn = await pingSession(topConfig);

            if (!isLoggedIn) {
                trackInfoEventValue(formConfig, 'session-expired-by-submit', true, '');
                throw new Error('SESSION-EXPIRED');
            }
        }

        const fileData: StagedFile[] = await UploadDocumentation.stageFiles(formValues, topConfig);

        let payload: ClaimFormPayload = await getSubmitPayload(
            formConfig,
            formValues,
            fileData,
            cmsResponseTime,
            claimNumber
        );
        setClaimFormPayload(payload);

        await submitForm(formConfig, topConfig, payload);

        trackSubmissionSuccess(formConfig);
        trackNavigationToReceiptPage(formConfig);

        // Deferred tracking by form values
        if (hasTrackingBySubmitFunction(formConfig.formId)) {
            const trackSubmitFunctionForForm = getTrackBySubmitFunction(formConfig.formId);
            trackSubmitFunctionForForm(formConfig, formValues);
        }

        // Clear saved form data
        await saveFormDataDelete(formConfig, topConfig);

        // Set to redirect mode after submit
        // This will hide the form and show a loading spinner before redirecting
        setIsRedirecting(true);

        // The tracking is using a pub-sub system that
        // empties its buffer "sometime"
        // If the pub-sub has not yet run, the redirection
        // to another page will prevent it from ever running
        // and thus not tracking anything
        //
        // By waiting a second - while not ideal - we make sure
        // that the tracking has completed
        const receiptPageKey = formConfig.receiptPage;
        const urlBase = conf.receiptPages[receiptPageKey];
        if (!urlBase) {
            throw new Error('Invalid receiptPage: ' + receiptPageKey);
        }
        setTimeout(() => {
            // Used by Hippo to make exit polls
            const url = urlBase + '?form=' + formConfig.formId;

            window.location.href = url;

            //Only resetForm immediately for mit pages - claimFormPayload of www forms needed to build PDF in receipt-page
            if (isMitPage) {
                resetForm();
            }
        }, 1000);
    } catch (error: any) {
        trackSubmissionFail(formConfig, error);
        setIsSubmitting(false);
        logError('Submit form - Error: ' + JSON.stringify({ error }));
        setError(error.message);
    } finally {
        // Do not track leaving customers after submission
        //
        // Should be handled by the React useEffect unmount function
        // window.addEventListener('beforeunload', () => {});

        trackTimeSpentOnFillingTheForm(formConfig, formStartTime);
    }
};
